// Site chips: small round icons over the canvas, one per wonder and one cluster per deposit hex.
// DOM elements, not textures, so glyphs and names stay sharp at any zoom.
//
//   sites ─► project the hex foot ─► size from camera height ─► lift above any city banner (leader line)
//         ─► screen-rect declutter + one chip per screen cell, capped ─► pooled elements, moved by transform
//
//   camera frame ─► nothing moved: one comparison
//                ─► chips moved < MOVE_PX: slide the placed chips by transform (no declutter, no DOM read)
//                ─► moved more, or the height stepped: declutter again, at most every RUN_EVERY frames
//
// Sizes come from ResizeObservers (the root, the city banners), never from a layout read in a frame.
//
// A wonder that has a 3D model shows no chip while the model stands; hovering the model brings its
// chip back with the name (and, on the danger list, since when). Chips have pointer-events: none, so
// the hover is a distance test against the projected sites.
//
// Truth: every chip carries `data-sources` and `data-site` (its record's key); unsourcedKeys() lists
// the chips drawn without a source id. Chip colour and glyph come from the record's category or
// resource kind, never from any quantity, and chips are hidden where the zoom bands say so.

import * as THREE from 'three/webgpu';
import { HEX_COUNT, hexCenter, hexCol, hexRow } from '../../core/grid.ts';
import type { ViewCore } from '../index.ts';
import { wrapDelta } from '../camera.ts';
import { hexTopHeight } from '../terrain/heights.ts';
import { Thinner, nearestTarget, sameBoxes, type Target } from './declutter.ts';
import { resourceGlyph, wonderGlyph } from './glyphs.ts';
import { KIND_NAMES, kindColor } from './palette.ts';
import {
  BANDS, bandAlpha, byPriority, clusterByHex, dangerRows, moreNames, resourceChipAlpha, wonderChipAlpha,
  type ResourceSite, type ResourceView, type WonderCategory, type WonderSite,
} from './plan.ts';

type Rect = [number, number, number, number];

/** Which way a chip's name plate opens from its medal. */
type Side = 'east' | 'west';

/** How `show` dresses a placed chip. `anchor` is the px from the rect's left edge to the marked point. */
interface Look {
  alpha: number;
  hover: boolean;
  side: Side;
  anchor: number;
}

interface Entry {
  el: HTMLElement;
  shown: boolean;
  stamp: number;
  alpha: number;
  hover: boolean;
  side: Side;
  /** Px of leader line written (0 = none) and the x it hangs from. */
  lead: number;
  lx: number;
  /** Keys of the records behind this chip that carry no source id. */
  unsourced: readonly string[];
}

interface WonderItem {
  site: WonderSite;
  /** Hex centre and the ground height under it, world units. */
  cx: number;
  cz: number;
  y: number;
  /** Whether the props module stands a model on it. */
  model: boolean;
}

interface Cluster {
  hex: number;
  sites: ResourceSite[];
  rank: number;
  cx: number;
  cz: number;
  y: number;
}

/** A chip placed by the last declutter, kept so a small camera move only slides it. */
interface Placed {
  entry: Entry;
  cx: number;
  cz: number;
  /** World height of the point the chip marks. */
  y: number;
  /** Px from the rect's left edge to that point, and the rect's height. */
  left: number;
  size: number;
  /** Where declutter moved the rect from its natural spot above the point. */
  dx: number;
  dy: number;
  /** Screen position of the point at the last declutter. */
  px: number;
  py: number;
  target: Target | null;
}

/** Per-declutter values shared by the placement methods. */
interface Frame {
  camX: number;
  height: number;
  ws: number;
  rs: number;
  names: boolean;
  modelAlpha: number;
  taken: Rect[];
  obstacles: Rect[];
  drawn: Set<string>;
  wonderRects: Map<number, Rect>;
  shown: number;
}

// Sizes (px): wonder and resource chips shrink a little with camera height (hex radii).
const WONDER_SIZE = { near: 20, far: 16, from: 20, to: 120 } as const;
const RESOURCE_SIZE = { near: 24, far: 15, from: 15, to: 110 } as const;
const NAME_BELOW = 12; // wonder names show under this height
const NAME_FONT = '600 11px "Alegreya Sans SC", "Alegreya Sans", "Trebuchet MS", sans-serif';
const NAME_TRACKING_PX = 0.44; // letter-spacing 0.04em of 11px
const NAME_FALLBACK_CHAR_PX = 6.6;
const NAME_PAD_PX = 23; // plate padding and border
const NAME_MAX_PX = 170;
const PLATE_OVERLAP_PX = 8; // the name plate tucks under the medal by this much
const OVERLAP_SHRINK = 0.18; // resource medals of one hex overlap by this share of their size
const GAP_PX = 6; // between the chip's foot and the point it marks
const NEIGHBOUR_GAP_PX = 4;
const PAD_PX = 3; // declutter margin
const EDGE_PX = 24; // a chip may hang this far off screen
const OPACITY_STEP = 0.04;
const LEAD_MIN_PX = 4; // a chip lifted less than this needs no leader line
const HOVER_PAD_PX = 6;

// How many chips show at once, and the screen cell that holds one.
const MAX_WONDER_CHIPS = 30;
const WONDER_CELL_PX = 44;
const PLAIN_CLUSTERS = { cap: 40, cell: 38 } as const;
const LENS_CLUSTERS = { cap: 120, cell: 26 } as const;

// Re-declutter when a placed chip moved this far, or the height changed by this log step.
const MOVE_PX = 8;
const HEIGHT_LOG_STEP = 0.02;
const RUN_EVERY = 2; // frames between two declutters while the camera moves

// World heights above the hex's ground (world units).
const LIFT_GROUND = 0.3;
const LIFT_MODEL = 0.95; // the monuments stand about 0.45 to 0.8 tall
const MODEL_CENTRE = 0.4;
const MODEL_RADIUS = 0.5;
const MODEL_HOVER_MIN_PX = 12;

const POOL_MAX = 700;

const STYLE_ID = 'wc-sites-style';
const STYLE = `
.wc-sites { position: absolute; inset: 0; pointer-events: none; overflow: hidden; --ws: 18px; --rs: 22px; }
.wc-site { position: absolute; left: 0; top: 0; display: flex; align-items: center; will-change: transform, opacity; }
.wc-site.wc-hot { z-index: 5; }
.wc-medal { position: relative; box-sizing: border-box; flex: none; border-radius: 50%; color: var(--ink); background: var(--bg); }
.wc-wonder .wc-medal { width: var(--ws); height: var(--ws); padding: calc(var(--ws) * 0.08); background: var(--bg);
  border: max(2px, calc(var(--ws) * 0.1)) solid var(--ring, #f3ead0);
  box-shadow: 0 0 0 1px rgba(28, 20, 8, 0.62), 0 1px 4px rgba(0, 0, 0, 0.45); }
.wc-wonder.wc-danger .wc-medal { --ring: #2d8fe0;
  box-shadow: 0 0 0 1px rgba(8, 24, 44, 0.8), 0 0 8px rgba(45, 143, 224, 0.8); }
.wc-medal svg { display: block; width: 100%; height: 100%; }
.wc-count { position: absolute; top: -6px; right: -9px; min-width: 15px; height: 15px; padding: 0 3px; box-sizing: border-box; border-radius: 8px;
  font: 700 10px/13px "Alegreya Sans SC", "Trebuchet MS", sans-serif; text-align: center; color: #3b2c12; background: #f3ead0; border: 1px solid #b89a55; }
.wc-sname { display: none; margin-left: -${PLATE_OVERLAP_PX}px; padding: 0 9px 0 12px; height: calc(var(--ws) * 0.8); line-height: calc(var(--ws) * 0.8 - 2px); max-width: ${NAME_MAX_PX}px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; box-sizing: border-box; border-radius: 0 4px 4px 0;
  font: ${NAME_FONT}; letter-spacing: 0.04em; text-transform: uppercase; color: #3b2c12;
  background: rgba(246, 238, 214, 0.96); border: 1px solid #b89a55; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.4); }
.wc-sites[data-names="1"] .wc-sname, .wc-hot .wc-sname { display: block; }
.wc-tip { display: none; position: absolute; left: 0; top: calc(100% + 4px); width: max-content; max-width: 260px; padding: 4px 8px; box-sizing: border-box;
  font: 500 11px/1.35 "Alegreya Sans", "Trebuchet MS", sans-serif; color: #3b2c12;
  background: rgba(246, 238, 214, 0.97); border: 1px solid #b89a55; border-radius: 4px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4); }
.wc-tip > div { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wc-danger .wc-tip { border-left: 3px solid #2d8fe0; }
.wc-hot .wc-tip { display: block; }
.wc-west { flex-direction: row-reverse; }
.wc-west .wc-sname { margin-left: 0; margin-right: -${PLATE_OVERLAP_PX}px; padding: 0 12px 0 9px; border-radius: 4px 0 0 4px; }
.wc-west .wc-tip { left: auto; right: 0; }
.wc-lead { display: none; position: absolute; left: var(--lx); top: 100%; width: 0; height: var(--lead); pointer-events: none;
  border-left: 1.5px solid rgba(246, 238, 214, 0.92); filter: drop-shadow(0 0 1px rgba(20, 14, 6, 0.85)); }
.wc-lead::after { content: ''; position: absolute; left: -3.5px; bottom: -3px; width: 5px; height: 5px; border-radius: 50%;
  background: #f6eed6; box-shadow: 0 0 0 1px rgba(20, 14, 6, 0.7); }
.wc-shift .wc-lead { display: block; }
.wc-res .wc-medal { width: var(--rs); height: var(--rs); padding: calc(var(--rs) * 0.12); border: max(1.5px, calc(var(--rs) * 0.07)) solid #efe5c8;
  box-shadow: 0 0 0 1px rgba(20, 16, 10, 0.6), 0 2px 5px rgba(0, 0, 0, 0.5); }
.wc-res .wc-medal + .wc-medal { margin-left: calc(var(--rs) * -${OVERLAP_SHRINK}); }
`;

/** Fill and glyph colours of a wonder chip by category (a record field, so colour by it is data). */
const WONDER_LOOK: Readonly<Record<WonderCategory, { bg: string; ink: string }>> = {
  cultural: { bg: '#e6cf94', ink: '#5c3d0c' },
  natural: { bg: '#c9e0b8', ink: '#1f5228' },
  mixed: { bg: '#cfdce6', ink: '#27455c' },
};
const INK_LIGHT = '#f7f2e3';
const INK_DARK = '#1b1b1b';
// Rec. 709 luma weights.
const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;
const LUMA_SPLIT = 0.42;

const TRANSLATE = /translate3d\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px/;
const BANNER_CLASS = 'wc-banner';

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Size in px at a camera height: `near` under `from`, `far` over `to`, smooth between. */
function sizeAt(s: { near: number; far: number; from: number; to: number }, height: number): number {
  const t = Math.min(1, Math.max(0, (height - s.from) / (s.to - s.from)));
  return Math.round(lerp(s.near, s.far, t * t * (3 - 2 * t)));
}

/** Dark ink on a light medal, light ink on a dark one. */
function inkFor(hex: string): string {
  const c = new THREE.Color(hex);
  const luma = LUMA_R * c.r + LUMA_G * c.g + LUMA_B * c.b;
  return luma > LUMA_SPLIT ? INK_DARK : INK_LIGHT;
}

function hits(a: Rect, b: Rect): boolean {
  return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
}

function padded(r: Rect): Rect {
  return [r[0] - PAD_PX, r[1] - PAD_PX, r[2] + PAD_PX, r[3] + PAD_PX];
}

function crowded(taken: readonly Rect[], r: Rect): boolean {
  const p = padded(r);
  return taken.some((o) => hits(p, o));
}

/** Lift a rect above any obstacle it covers (the chip goes over a city banner, not under it). */
function clearOf(r: Rect, obstacles: readonly Rect[]): Rect {
  let out = r;
  for (let pass = 0; pass < 2; pass++) {
    const hit = obstacles.find((o) => hits(out, o));
    if (!hit) {
      return out;
    }

    const h = out[3] - out[1];
    out = [out[0], hit[1] - h - NEIGHBOUR_GAP_PX, out[2], hit[1] - NEIGHBOUR_GAP_PX];
  }
  return out;
}

const nameWidths = new Map<string, number>();
let ruler: CanvasRenderingContext2D | null | undefined;

/** Width of a name plate in px: measured once per name on a canvas, never from the DOM. */
function nameWidth(name: string): number {
  const hit = nameWidths.get(name);
  if (hit !== undefined) {
    return hit;
  }

  if (ruler === undefined) {
    ruler = document.createElement('canvas').getContext('2d');
    if (ruler) {
      ruler.font = NAME_FONT;
    }
  }

  const text = name.toUpperCase();
  const px = ruler ? ruler.measureText(text).width + text.length * NAME_TRACKING_PX : text.length * NAME_FALLBACK_CHAR_PX;
  const width = Math.min(NAME_MAX_PX, Math.ceil(px) + NAME_PAD_PX);
  nameWidths.set(name, width);
  return width;
}

function offscreen(r: Rect, w: number, h: number): boolean {
  return r[2] < -EDGE_PX || r[0] > w + EDGE_PX || r[3] < -EDGE_PX || r[1] > h + EDGE_PX;
}

function moveTo(el: HTMLElement, x: number, y: number): void {
  el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
}

export class SiteChips {
  private readonly root = document.createElement('div');
  private readonly camera: THREE.PerspectiveCamera;
  private readonly banners: HTMLElement;
  private readonly canvas: HTMLElement;
  private readonly pool = new Map<string, Entry>();
  private readonly bannerSize = new Map<Element, [number, number]>();
  private readonly rootWatch: ResizeObserver;
  private readonly bannerWatch: ResizeObserver;
  private readonly bannerAdds: MutationObserver;
  private readonly v = new THREE.Vector3();
  private readonly lastPose = new Float64Array(16);
  private readonly wonderThin = new Thinner(WONDER_CELL_PX);
  private readonly placed: Placed[] = [];
  private readonly targets: Target[] = [];
  private sx = 0;
  private sy = 0;
  private w = 0;
  private h = 0;
  private hq = NaN;
  private frames = 0;
  private lastRun = -RUN_EVERY;
  private forced = true;
  private pending = false;
  private moving = false;
  private bannersDirty = false;
  private obstacles: Rect[] = [];
  private wonders: WonderItem[] = [];
  private byKey = new Map<string, WonderItem>();
  private clusters: Cluster[] = [];
  private view: ResourceView = 'plain';
  private stamp = 0;
  private ws = 0;
  private rs = 0;
  private names = false;
  private hoverKey = '';
  private pointer: { x: number; y: number } | null = null;
  private drawnUnsourced: string[] = [];
  private readonly shapes: ViewCore['shapes'];

  constructor(core: ViewCore) {
    this.camera = core.camera.camera;
    this.banners = core.labels.root;
    this.canvas = core.engine.renderer.domElement;
    this.shapes = core.shapes;
    this.root.className = 'wc-sites';
    // Under the labels: a banner reads over a chip, never the reverse.
    core.labels.root.parentElement?.insertBefore(this.root, core.labels.root);
    this.addStyle();

    this.rootWatch = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1].contentRect;
      this.w = box.width;
      this.h = box.height;
      this.forced = true;
    });
    this.rootWatch.observe(this.root);

    // Banner sizes arrive here, after layout, so a frame never asks the DOM for one.
    this.bannerWatch = new ResizeObserver((entries) => {
      for (const e of entries) {
        const box = e.borderBoxSize?.[0];
        this.bannerSize.set(e.target, box ? [box.inlineSize, box.blockSize] : [e.contentRect.width, e.contentRect.height]);
      }
      this.bannersDirty = true;
    });

    // The labels layer decides which banners show and where, on its own clock: a still camera
    // can still see the banners change, and the chips must dodge the new ones.
    this.bannerAdds = new MutationObserver((records) => {
      this.bannersDirty = true;
      for (const r of records) {
        r.addedNodes.forEach((n) => this.watchBanner(n));
        r.removedNodes.forEach((n) => this.forgetBanner(n));
      }
    });
    this.bannerAdds.observe(this.banners, { childList: true, attributes: true, attributeFilter: ['style'], subtree: true });
    this.banners.childNodes.forEach((n) => this.watchBanner(n));

    this.canvas.addEventListener('pointermove', this.onMove);
    this.canvas.addEventListener('pointerleave', this.onLeave);
  }

  setSites(wonders: readonly WonderSite[], resources: readonly ResourceSite[], modelled: ReadonlySet<string>): void {
    for (const e of this.pool.values()) {
      e.el.remove();
    }
    this.pool.clear();
    this.placed.length = 0;
    this.targets.length = 0;

    this.wonders = [...wonders]
      .filter((site) => site.hex >= 0 && site.hex < HEX_COUNT)
      .sort(byPriority)
      .map((site) => {
        const c = hexCenter(hexCol(site.hex), hexRow(site.hex));
        return { site, cx: c.x, cz: c.z, y: hexTopHeight(this.shapes, site.hex), model: modelled.has(site.key) };
      });
    this.byKey = new Map(this.wonders.map((item) => [item.site.key, item]));

    this.clusters = [...clusterByHex(resources)]
      .filter(([hex]) => hex >= 0 && hex < HEX_COUNT)
      .map(([hex, sites]) => {
        const c = hexCenter(hexCol(hex), hexRow(hex));
        return { hex, sites, rank: Math.min(...sites.map((s) => s.rank)), cx: c.x, cz: c.z, y: hexTopHeight(this.shapes, hex) };
      });
    this.clusters.sort((a, b) => a.rank - b.rank);

    this.hoverKey = '';
    this.forced = true;
  }

  setView(view: ResourceView): void {
    this.view = view;
    this.forced = true;
  }

  /** Keys of the chips drawn this frame whose records carry no source id. */
  unsourcedKeys(): readonly string[] {
    return this.drawnUnsourced;
  }

  /** Place and declutter the chips for this frame; a small camera move only slides them. */
  update(camX: number, height: number): void {
    if (this.w === 0 || this.h === 0) {
      return;
    }

    this.camera.updateMatrixWorld();
    const posed = this.poseMoved();
    const settled = this.moving && !posed;
    this.moving = posed;
    const hq = Math.round(Math.log(height) / HEIGHT_LOG_STEP);
    const stepped = hq !== this.hq;
    const restless = this.bannersDirty && !posed && !sameBoxes(this.obstacles, this.bannerRects());
    this.bannersDirty = false;
    if (!this.forced && !posed && !stepped && !this.pending && !settled && !restless) {
      return;
    }

    this.frames++;
    let run = this.forced;
    if (!run) {
      const moved = posed ? this.slide(camX) : 0;
      if (moved > MOVE_PX || stepped || settled || restless || (posed && this.placed.length === 0)) {
        this.pending = true;
      }
      run = this.pending && this.frames - this.lastRun >= RUN_EVERY;
    }
    if (!run) {
      return;
    }

    this.forced = false;
    this.pending = false;
    this.lastRun = this.frames;
    this.hq = hq;
    this.declutter(camX, height);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerleave', this.onLeave);
    this.rootWatch.disconnect();
    this.bannerWatch.disconnect();
    this.bannerAdds.disconnect();
    this.root.remove();
    this.pool.clear();
    this.bannerSize.clear();
    this.placed.length = 0;
    this.targets.length = 0;
    this.wonders = [];
    this.byKey.clear();
    this.clusters = [];
  }

  private addStyle(): void {
    if (document.getElementById(STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
  }

  private watchBanner(node: Node): void {
    if (node instanceof HTMLElement && node.classList.contains(BANNER_CLASS)) {
      this.bannerWatch.observe(node);
    }
  }

  private forgetBanner(node: Node): void {
    if (node instanceof HTMLElement) {
      this.bannerWatch.unobserve(node);
      this.bannerSize.delete(node);
    }
  }

  private readonly onMove = (e: PointerEvent): void => {
    // A drag pans or turns the map: no hover then.
    this.pointer = e.buttons === 0 ? { x: e.offsetX, y: e.offsetY } : null;
    this.pick();
  };

  private readonly onLeave = (): void => {
    this.pointer = null;
    this.pick();
  };

  /** The site under the pointer; a change makes the next frame declutter at once. */
  private pick(): void {
    const t = this.pointer ? nearestTarget(this.targets, this.pointer.x, this.pointer.y) : null;
    const key = t ? t.key : '';
    if (key === this.hoverKey) {
      return;
    }

    this.hoverKey = key;
    this.forced = true;
  }

  /** Whether the camera matrix differs from the last frame's; remembers the new one. */
  private poseMoved(): boolean {
    const m = this.camera.matrixWorld.elements;
    let moved = false;
    for (let i = 0; i < m.length; i++) {
      moved = moved || this.lastPose[i] !== m[i];
      this.lastPose[i] = m[i];
    }
    return moved;
  }

  /** Project a world point to (this.sx, this.sy) px; false when it is behind the camera or past the far plane. */
  private toScreen(cx: number, y: number, cz: number, camX: number): boolean {
    this.v.set(camX + wrapDelta(camX, cx), y, cz).project(this.camera);
    if (this.v.z < -1 || this.v.z > 1) {
      return false;
    }

    this.sx = (this.v.x * 0.5 + 0.5) * this.w;
    this.sy = (-this.v.y * 0.5 + 0.5) * this.h;
    return true;
  }

  /** Move every placed chip with its point; the farthest one's shift since the last declutter, in px. */
  private slide(camX: number): number {
    let moved = 0;
    for (const p of this.placed) {
      if (!this.toScreen(p.cx, p.y, p.cz, camX)) {
        moved = Infinity;
        continue;
      }

      moved = Math.max(moved, Math.abs(this.sx - p.px), Math.abs(this.sy - p.py));
      const x = this.sx - p.left + p.dx;
      const y = this.sy - p.size - GAP_PX + p.dy;
      moveTo(p.entry.el, x, y);
      if (p.target) {
        p.target.x = x + p.left;
        p.target.y = y + p.size / 2;
      }
    }
    return moved;
  }

  private declutter(camX: number, height: number): void {
    const ws = sizeAt(WONDER_SIZE, height);
    const rs = sizeAt(RESOURCE_SIZE, height);
    const names = height < NAME_BELOW;
    if (ws !== this.ws) {
      this.ws = ws;
      this.root.style.setProperty('--ws', `${ws}px`);
    }
    if (rs !== this.rs) {
      this.rs = rs;
      this.root.style.setProperty('--rs', `${rs}px`);
    }
    if (names !== this.names) {
      this.names = names;
      this.root.dataset.names = names ? '1' : '0';
    }

    this.stamp++;
    this.obstacles = this.bannerRects();
    this.placed.length = 0;
    this.targets.length = 0;
    this.wonderThin.reset();
    const f: Frame = {
      camX, height, ws, rs, names,
      modelAlpha: bandAlpha(height, BANDS.wonderModel),
      taken: [],
      obstacles: this.obstacles,
      drawn: new Set(),
      wonderRects: new Map(),
      shown: 0,
    };

    // The hovered site first: it always shows, and the others make room for it.
    const hovered = this.byKey.get(this.hoverKey);
    if (hovered) {
      this.placeWonder(f, hovered, 'hover');
    }
    for (const item of this.wonders) {
      if (item !== hovered) {
        this.placeWonder(f, item, 'normal');
      }
    }
    this.placeClusters(f);

    this.drawnUnsourced = [...f.drawn];
    this.sweep();
    this.pick();
  }

  private placeWonder(f: Frame, item: WonderItem, mode: 'hover' | 'normal'): void {
    const site = item.site;
    const hover = mode === 'hover';
    const standing = item.model && f.modelAlpha > 0;
    const alpha = hover ? 1 : wonderChipAlpha(site, f.height) * (item.model ? 1 - f.modelAlpha : 1);
    const wantsChip = alpha > 0 && (hover || f.shown < MAX_WONDER_CHIPS);
    if (!wantsChip && !standing) {
      return;
    }

    const lift = LIFT_GROUND + (item.model ? LIFT_MODEL * f.modelAlpha : 0);
    if (!this.toScreen(item.cx, item.y + lift, item.cz, f.camX)) {
      return;
    }

    const px = this.sx;
    const py = this.sy;
    if (standing) {
      this.addModelTarget(f, item);
    }
    if (!wantsChip) {
      return;
    }

    const size = f.ws;
    const width = hover || f.names ? size + nameWidth(site.name) - PLATE_OVERLAP_PX : size;

    // A hovered chip opens its plate to the left: the pointer's card hangs to the right and below.
    const side: Side = hover && px + size / 2 - width >= -EDGE_PX ? 'west' : 'east';
    const anchor = side === 'west' ? width - size / 2 : size / 2;
    const natural: Rect = [px - anchor, py - size - GAP_PX, px - anchor + width, py - GAP_PX];
    if (offscreen(natural, this.w, this.h)) {
      return;
    }

    const rect = clearOf(natural, f.obstacles);
    if (!hover && (crowded(f.taken, rect) || !this.wonderThin.take(px, py))) {
      return;
    }
    if (hover) {
      this.wonderThin.take(px, py);
    }

    f.taken.push(rect);
    f.wonderRects.set(site.hex, rect);
    f.shown++;
    const entry = this.wonderEntry(item);
    this.show(entry, rect, natural, { alpha, hover, side, anchor });
    const target: Target = { key: site.key, x: rect[0] + anchor, y: rect[1] + size / 2, r: size / 2 + HOVER_PAD_PX };
    this.targets.push(target);
    this.placed.push({
      entry, cx: item.cx, cz: item.cz, y: item.y + lift, left: anchor, size,
      dx: rect[0] - natural[0], dy: rect[1] - natural[1], px, py, target,
    });
    if (!site.sourced) {
      f.drawn.add(site.key);
    }
  }

  /** The disc on the screen where the pointer counts as on a standing model. */
  private addModelTarget(f: Frame, item: WonderItem): void {
    if (!this.toScreen(item.cx, item.y + MODEL_CENTRE, item.cz, f.camX)) {
      return;
    }

    const x = this.sx;
    const y = this.sy;
    const reach = this.toScreen(item.cx + MODEL_RADIUS, item.y + MODEL_CENTRE, item.cz, f.camX) ? Math.abs(this.sx - x) : 0;
    this.targets.push({ key: item.site.key, x, y, r: Math.max(MODEL_HOVER_MIN_PX, reach) });
  }

  /** Deposit clusters: beside the hex's wonder chip if it has one, else above the hex. */
  private placeClusters(f: Frame): void {
    const alpha = resourceChipAlpha(f.height, this.view);
    if (alpha === 0) {
      return;
    }

    const limits = this.view === 'lens' ? LENS_CLUSTERS : PLAIN_CLUSTERS;
    const thin = new Thinner(limits.cell);
    let count = 0;
    for (const c of this.clusters) {
      if (count >= limits.cap) {
        break;
      }
      if (!this.toScreen(c.cx, c.y + LIFT_GROUND, c.cz, f.camX)) {
        continue;
      }

      const px = this.sx;
      const py = this.sy;
      const size = f.rs;
      const width = size * (c.sites.length - (c.sites.length - 1) * OVERLAP_SHRINK);
      const natural: Rect = [px - width / 2, py - size - GAP_PX, px + width / 2, py - GAP_PX];
      if (offscreen(natural, this.w, this.h)) {
        continue;
      }

      const beside = f.wonderRects.get(c.hex);
      const rect = beside ? this.beside(beside, width, size) : clearOf(natural, f.obstacles);
      if (crowded(f.taken, rect) || !thin.take(px, py)) {
        continue;
      }

      f.taken.push(rect);
      count++;
      const entry = this.clusterEntry(c);
      this.show(entry, rect, natural, { alpha, hover: false, side: 'east', anchor: width / 2 });
      this.placed.push({
        entry, cx: c.cx, cz: c.cz, y: c.y + LIFT_GROUND, left: width / 2, size,
        dx: rect[0] - natural[0], dy: rect[1] - natural[1], px, py, target: null,
      });
      for (const key of entry.unsourced) {
        f.drawn.add(key);
      }
    }
  }

  /** A rect to the right of `anchor`, level with its foot. */
  private beside(anchor: Rect, width: number, size: number): Rect {
    const x0 = anchor[2] + NEIGHBOUR_GAP_PX;
    const y1 = anchor[3];
    return [x0, y1 - size, x0 + width, y1];
  }

  private show(e: Entry, rect: Rect, natural: Rect, look: Look): void {
    const { alpha, hover, side, anchor } = look;
    e.stamp = this.stamp;
    moveTo(e.el, rect[0], rect[1]);
    if (!e.shown) {
      e.el.style.display = '';
      e.shown = true;
    }
    if (Math.abs(alpha - e.alpha) >= OPACITY_STEP || (alpha === 1) !== (e.alpha === 1)) {
      e.el.style.opacity = alpha.toFixed(2);
      e.alpha = alpha;
    }
    if (hover !== e.hover) {
      e.hover = hover;
      e.el.classList.toggle('wc-hot', hover);
    }
    if (side !== e.side) {
      e.side = side;
      e.el.classList.toggle('wc-west', side === 'west');
    }

    // A chip lifted off its point (over a city banner) keeps a line down to it.
    const lifted = natural[1] - rect[1];
    const lead = lifted > LEAD_MIN_PX ? Math.round(lifted + GAP_PX) : 0;
    const lx = Math.round(natural[0] - rect[0] + anchor);
    if (lead === e.lead && (lead === 0 || lx === e.lx)) {
      return;
    }

    e.lead = lead;
    e.lx = lx;
    e.el.classList.toggle('wc-shift', lead > 0);
    if (lead > 0) {
      e.el.style.setProperty('--lead', `${lead}px`);
      e.el.style.setProperty('--lx', `${lx}px`);
    }
  }

  /** Hide what was not placed this frame; drop hidden elements when the pool is large. */
  private sweep(): void {
    const drop: string[] = [];
    for (const [key, e] of this.pool) {
      if (e.stamp === this.stamp) {
        continue;
      }
      if (e.shown) {
        e.el.style.display = 'none';
        e.shown = false;
      }
      if (this.pool.size > POOL_MAX) {
        drop.push(key);
      }
    }
    for (const key of drop) {
      this.pool.get(key)?.el.remove();
      this.pool.delete(key);
    }
  }

  /** City banners on screen: position from the labels' transform, size from the resize observer. */
  private bannerRects(): Rect[] {
    const out: Rect[] = [];
    const kids = this.banners.children;
    for (let i = 0; i < kids.length; i++) {
      const el = kids[i] as HTMLElement;
      if (el.style.display === 'none' || !el.classList.contains(BANNER_CLASS)) {
        continue;
      }

      const size = this.bannerSize.get(el);
      const m = size ? TRANSLATE.exec(el.style.transform) : null;
      if (!size || !m || size[0] === 0) {
        continue;
      }

      const x = Number(m[1]);
      const y = Number(m[2]);
      out.push([x, y, x + size[0], y + size[1]]);
    }
    return out;
  }

  private wonderEntry(item: WonderItem): Entry {
    const site = item.site;
    const hit = this.pool.get(site.key);
    if (hit) {
      return hit;
    }

    const el = document.createElement('div');
    el.className = site.danger ? 'wc-site wc-wonder wc-danger' : 'wc-site wc-wonder';
    el.dataset.site = site.key;
    el.dataset.kind = site.category;
    el.dataset.sources = site.sources.join(' ');
    el.dataset.names = site.names.join(' | ');

    const look = WONDER_LOOK[site.category];
    const medal = document.createElement('div');
    medal.className = 'wc-medal';
    medal.style.setProperty('--bg', look.bg);
    medal.style.setProperty('--ink', look.ink);
    medal.innerHTML = wonderGlyph(site.category);
    if (site.extra > 0) {
      const count = document.createElement('span');
      count.className = 'wc-count';
      count.textContent = `+${site.extra}`;
      medal.appendChild(count);
    }

    const plate = document.createElement('span');
    plate.className = 'wc-sname';
    plate.textContent = site.name;
    el.append(medal, plate);

    const rows = site.dangers.flatMap((d) => dangerRows(site.name, d));
    // A danger-listed name already has its own rows.
    const listed = new Set(site.dangers.map((d) => d.name));
    const others = moreNames(site.names.filter((n, i) => i === 0 || !listed.has(n)));
    if (others.length > 0) {
      rows.push('Also here:', ...others);
    }
    if (rows.length > 0) {
      const tip = document.createElement('div');
      tip.className = 'wc-tip';
      for (const text of rows) {
        const row = document.createElement('div');
        row.textContent = text;
        tip.appendChild(row);
      }
      el.appendChild(tip);
    }

    const leader = document.createElement('i');
    leader.className = 'wc-lead';
    el.appendChild(leader);
    return this.add(site.key, el, site.sourced ? [] : [site.key]);
  }

  private clusterEntry(c: Cluster): Entry {
    const key = `c:${c.hex}`;
    const hit = this.pool.get(key);
    if (hit) {
      return hit;
    }

    const el = document.createElement('div');
    el.className = 'wc-site wc-res';
    el.dataset.hex = String(c.hex);
    const unsourced: string[] = [];
    for (const site of [...c.sites].sort((a, b) => a.kind - b.kind)) {
      const bg = kindColor(site.kind);
      const medal = document.createElement('div');
      medal.className = 'wc-medal';
      medal.dataset.site = site.key;
      medal.dataset.kind = (KIND_NAMES[site.kind] ?? '').toLowerCase();
      medal.dataset.sources = site.sources.join(' ');
      medal.style.setProperty('--bg', bg);
      medal.style.setProperty('--ink', inkFor(bg));
      medal.innerHTML = resourceGlyph(site.kind);
      el.appendChild(medal);
      if (!site.sourced) {
        unsourced.push(site.key);
      }
    }

    const leader = document.createElement('i');
    leader.className = 'wc-lead';
    el.appendChild(leader);
    return this.add(key, el, unsourced);
  }

  private add(key: string, el: HTMLElement, unsourced: readonly string[]): Entry {
    el.style.display = 'none';
    this.root.appendChild(el);
    const e: Entry = { el, shown: false, stamp: 0, alpha: -1, hover: false, side: 'east', lead: 0, lx: 0, unsourced };
    this.pool.set(key, e);
    return e;
  }
}
